'use strict';

/**
 * product-info-component: syncs variant → price, Klarna, picker UI, shipping, buttons, media.
 */
(() => {
  const SELECTORS = {
    productJson: 'script[type="application/json"][data-product-json]',
    priceSection: '[data-product-price-root]',
    variantId: '[data-add-to-cart-var-id]',
    mainImage: '[data-product-main-image]',
    shippingNote: '[data-product-shipping-note]',
    variantPicker: '[data-product-variant-picker]',
    addToCart: '[data-add-to-cart-btn]',
    buyNow: '[data-buy-now-btn]',
    klarna: '[data-product-klarna]',
  };

  const getHelpers = () => window._theme?.Helpers || {};

  const formatMoney = (amount) => {
    const { formatPrice } = getHelpers();
    return typeof formatPrice === 'function' ? formatPrice(Number(amount)) : '';
  };

  const sizedImageUrl = (url, width) => {
    if (!url || typeof url !== 'string') return '';

    return url
      .replace(/_(\d+)x(?=\.(jpg|jpeg|png|webp|gif))/i, '')
      .replace(/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i, `_${width}x.$1$2`);
  };

  const getDefaultVariant = (product) => {
    if (!product?.variants?.length) return null;

    const selectedId = product.selected_or_first_available_variant?.id;
    if (selectedId != null) {
      const match = product.variants.find((v) => v.id === selectedId);
      if (match) return match;
    }

    return product.variants.find((v) => v.available) || product.variants[0];
  };

  class ProductInfoComponent extends HTMLElement {
    constructor() {
      super();
      this.product = null;
      this.selectors = {};
      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    connectedCallback() {
      this.cacheSelectors();
      this.product = this.getProductData();

      this.addEventListener('variant-picker-component:change', this.handleVariantChange);

      if (this.product) {
        this.onVariantChange(getDefaultVariant(this.product));
      }
    }

    disconnectedCallback() {
      this.removeEventListener('variant-picker-component:change', this.handleVariantChange);
      this.product = null;
      this.selectors = {};
    }

    cacheSelectors() {
      for (const [key, selector] of Object.entries(SELECTORS)) {
        this.selectors[key] = this.querySelector(selector);
      }
    }

    getProductData() {
      const scriptEl = this.selectors.productJson;
      if (!scriptEl?.textContent) return null;

      try {
        const data = JSON.parse(scriptEl.textContent);
        return data && typeof data === 'object' ? data : null;
      } catch {
        return null;
      }
    }

    handleVariantChange(event) {
      if (event.target.tagName !== 'VARIANT-PICKER-COMPONENT') return;

      const currentVariant = event.detail?.currentVariant;
      const currentVariantId = event.detail?.currentVariantId;
      const selectedOptionValues = event.detail?.selectedOptionValues;

      if (!currentVariant || !this.product) return;

      this.onVariantChange(currentVariant);
    }

    onVariantChange(currentVariant) {
      if (!this.product) return;

      this.updatePrice(currentVariant);
      this.updateKlarna(currentVariant);
      this.updateVariantPicker(currentVariant);
      this.updateShippingNote(currentVariant);
      this.updateButtons(currentVariant);
      this.updateMedia(currentVariant);

      document.dispatchEvent(
        new CustomEvent('variant:changed', {
          detail: {
            productId: this.product.id,
            variantId: currentVariant?.id || null,
          },
        })
      );
    }

    /* ---------- PRICE ---------- */
    updatePrice(currentVariant) {
      const section = this.selectors.priceSection;
      if (!section) return;

      const unavailableEl = section.querySelector('[data-product-price-unavailable]');
      const displayEl = section.querySelector('[data-product-price-main]');
      const currentEl = section.querySelector('[data-product-price-current]');
      const compareEl = section.querySelector('[data-product-price-compare]');
      const saveEl = section.querySelector('[data-product-price-save]');

      const { formatPrice } = getHelpers();

      if (!currentVariant) {
        if (unavailableEl) {
          unavailableEl.hidden = false;
          unavailableEl.textContent = 'Unavailable';
        }
        if (displayEl) {
          displayEl.hidden = true;
          displayEl.removeAttribute('data-on-sale');
        }
        return;
      }

      if (typeof formatPrice !== 'function') {
        return;
      }

      if (unavailableEl) unavailableEl.hidden = true;
      if (displayEl) displayEl.hidden = false;

      const price = currentVariant.price;
      const comparePrice = currentVariant.compare_at_price;
      const isOnSale = Boolean(comparePrice && comparePrice > price);

      if (currentEl) currentEl.textContent = formatMoney(price / 100);

      if (compareEl) {
        compareEl.hidden = !isOnSale;
        compareEl.textContent = isOnSale ? formatMoney(comparePrice / 100) : '';
      }

      if (saveEl) {
        saveEl.hidden = !isOnSale;
        saveEl.textContent = isOnSale ? `Save ${formatMoney((comparePrice - price) / 100)}` : '';
      }

      if (displayEl) {
        if (isOnSale) displayEl.setAttribute('data-on-sale', '');
        else displayEl.removeAttribute('data-on-sale');
      }
    }

    /* ---------- KLARNA ---------- */
    updateKlarna(currentVariant) {
      const klarnaEl = this.selectors.klarna;
      if (!klarnaEl || !currentVariant) return;

      klarnaEl.setAttribute('data-purchase-amount', String(currentVariant.price));
      getHelpers().refreshKlarna?.();
    }

    /* ---------- VARIANT PICKER ---------- */
    updateVariantPicker(currentVariant) {
      const product = this.product;
      if (!currentVariant || !product) return;

      const root = this.selectors.variantPicker || this;

      const labelEls = root.querySelectorAll('[data-variant-picker-label]');
      if (!labelEls.length) return;

      const opt = product.options?.[0];
      if (!opt || opt.name === 'Title') {
        labelEls.forEach((el) => {
          el.textContent = '';
        });
        return;
      }

      if (typeof window._theme?.formatVariantPickerOptionLabel === 'function') {
        labelEls.forEach((el, idx) => {
          const optVal = currentVariant[`option${idx + 1}`];
          el.textContent = window._theme.formatVariantPickerOptionLabel(optVal);
        });
        return;
      }

      labelEls.forEach((el, idx) => {
        const optVal = currentVariant[`option${idx + 1}`];
        el.textContent = optVal != null ? String(optVal) : '';
      });
    }

    /* ---------- SHIPPING ---------- */
    updateShippingNote(currentVariant) {
      const container = this.selectors.shippingNote;
      if (!container || !currentVariant) return;

      const dialog = this.closest('quickview-dialog');
      const thresholdAttr = dialog ? dialog.getAttribute('data-freeship-threshold') : null;
      const threshold = thresholdAttr ? parseInt(thresholdAttr.replace(/[^0-9.]/g, ''), 10) : NaN;

      const line = container.querySelector('[data-product-shipping-line]');
      const before = container.querySelector('[data-product-shipping-before]');
      const highlight = container.querySelector('[data-product-shipping-highlight]');
      const { formatPrice } = getHelpers();

      if (
        !Number.isFinite(threshold) ||
        threshold < 0 ||
        typeof formatPrice !== 'function' ||
        !line ||
        !before ||
        !highlight
      ) {
        container.hidden = true;
        line.hidden = true;
        return;
      }

      if (currentVariant.price >= threshold) {
        container.hidden = false;
        line.hidden = false;
        before.textContent = 'Eligible for ';
        highlight.textContent = 'Free Shipping';
        highlight.hidden = false;
        return;
      }

      if (threshold > 0) {
        container.hidden = false;
        line.hidden = false;
        const remaining = threshold - currentVariant.price;
        before.textContent = `Spend ${formatMoney(remaining / 100)} more for free shipping`;
        highlight.textContent = '';
        highlight.hidden = true;
      } else {
        container.hidden = true;
        line.hidden = true;
      }
    }

    /* ---------- BUTTONS ---------- */
    updateButtons(currentVariant) {
      const { addToCart, buyNow, variantId } = this.selectors;

      if (currentVariant) {
        const variantIdStr = String(currentVariant.id);
        if (variantId) variantId.value = variantIdStr;
        this.querySelectorAll('form input[name="id"]').forEach((input) => {
          input.value = variantIdStr;
        });
      }

      const isAvailable = Boolean(currentVariant?.available);

      for (const btn of [addToCart, buyNow]) {
        if (!btn) continue;
        btn.disabled = !isAvailable;
        btn.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
      }
      const label = addToCart?.querySelector('.quickview-dialog__btn-label');
      if (!label || !addToCart) return;

      if (!addToCart.dataset.atcDefaultLabel) {
        const t = label.textContent.trim();
        if (t) addToCart.dataset.atcDefaultLabel = t;
      }

      label.textContent = isAvailable
        ? addToCart.dataset.atcDefaultLabel || 'Add to Cart'
        : 'Sold out';
    }

    /* ---------- MEDIA ---------- */
    updateMedia(currentVariant) {
      const img = this.selectors.mainImage;
      if (!img || !currentVariant?.featured_image?.src) return;

      img.src = sizedImageUrl(currentVariant.featured_image.src, 1000);
      if (this.product?.title) img.alt = this.product.title;
    }
  }
  if (!customElements.get('product-info-component')) {
    customElements.define('product-info-component', ProductInfoComponent);
  }
})();
